import axios from 'axios';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const AUTOPLAY_MS = 5000;
const SWIPE_THRESHOLD = 50;

export default function Hero() {
  const { t } = useTranslation();
  const localhost = process.env.REACT_APP_LOCALHOST;
  const localimg = process.env.REACT_APP_LOCALHOST_IMG;

  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  const [shown, setShown] = useState(3);
  const [index, setIndex] = useState(0);
  const dragStartX = useRef(null);

  const fetchSlides = async () => {
    try {
      const res = await axios.get(localhost + 'all-carousels');
      setSlides(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      // console.error('Error fetching carousels:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Responsive: 3 images on desktop, 2 on tablet, 1 on mobile
  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setShown(1);
      else if (window.innerWidth < 1024) setShown(2);
      else setShown(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const total = slides.length;
  const visibleCount = Math.min(shown, total || 1);
  const maxIndex = Math.max(0, total - visibleCount);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const next = useCallback(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);
  const prev = useCallback(() => setIndex((i) => (i <= 0 ? maxIndex : i - 1)), [maxIndex]);

  useEffect(() => {
    if (total <= visibleCount) return;
    const id = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [next, total, visibleCount]);

  const onPointerDown = (e) => { dragStartX.current = e.clientX; };
  const onPointerUp = (e) => {
    if (dragStartX.current === null) return;
    const delta = e.clientX - dragStartX.current;
    dragStartX.current = null;
    if (delta < -SWIPE_THRESHOLD) next();
    else if (delta > SWIPE_THRESHOLD) prev();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center" style={{ height: 'clamp(420px, 80vh, 820px)' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (total === 0) return null;

  // Track math — keeps each image at its natural aspect (object-cover, no stretch):
  //   trackW    = (total / visibleCount) * 100   track width relative to container
  //   slideW    = 100 / total                    each slide as % of track
  //   offsetPct = (index / total) * 100          translate offset as % of track
  const trackW = (total / visibleCount) * 100;
  const slideW = 100 / total;
  const offsetPct = (index / total) * 100;
  const showArrows = total > visibleCount;

  return (
    <div
      className="relative w-full overflow-hidden select-none text-white"
      style={{
        height: 'clamp(420px, 80vh, 820px)',
        touchAction: 'pan-y',
        cursor: showArrows ? 'grab' : 'default',
      }}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {/* Sliding track */}
      <div
        className="flex h-full"
        style={{
          width: `${trackW}%`,
          transform: `translateX(-${offsetPct}%)`,
          transition: 'transform 0.65s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {slides.map((slide, i) => (
          <div key={i} className="relative h-full flex-shrink-0" style={{ width: `${slideW}%` }}>
            <img
              src={localimg + slide.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
              loading={i < visibleCount ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>

      {/* Gradient overlay — darkens the bottom for text legibility without hiding the images */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.2) 42%, transparent 66%)' }}
      />

      {/* Fixed brand overlay — bottom left */}
      <div
        className="absolute bottom-0 left-0 z-10 px-6 sm:px-12 pb-10 sm:pb-14"
        style={{ width: 'min(560px, 88vw)' }}
      >
        <p className="uppercase mb-2.5 text-white/70" style={{ fontSize: '10px', letterSpacing: '0.4em' }}>
          {t('hero-tagline')}
        </p>
        <h1 className="font-josefin-sans mb-3 leading-none" style={{ fontSize: 'clamp(34px, 5vw, 68px)', letterSpacing: '0.04em' }}>
          {t('hero-title')}
        </h1>
        <p className="text-white/75 mb-6 font-light" style={{ fontSize: '13px', lineHeight: 1.55, maxWidth: '420px' }}>
          {t('hero-subtitle')}
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href="/shop"
            onClick={(e) => e.stopPropagation()}
            className="inline-block bg-white text-black uppercase font-medium transition-colors duration-300 hover:bg-white/85"
            style={{ fontSize: '11px', letterSpacing: '0.18em', padding: '13px 30px' }}
          >
            {t('shop-now')}
          </a>
          <a
            href="/new-arrivals"
            onClick={(e) => e.stopPropagation()}
            className="inline-block uppercase font-medium border border-white/60 text-white transition-colors duration-300 hover:bg-white/10"
            style={{ fontSize: '11px', letterSpacing: '0.18em', padding: '13px 30px' }}
          >
            {t('new-arrivals')}
          </a>
        </div>
      </div>

      {/* Left arrow */}
      {showArrows && (
        <button
          type="button"
          onClick={prev}
          aria-label="Previous"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
          style={{ background: 'rgba(255,255,255,0.92)', color: '#111', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}
        >
          <ChevronLeftIcon className="w-[18px] h-[18px]" strokeWidth={1.8} />
        </button>
      )}

      {/* Right arrow */}
      {showArrows && (
        <button
          type="button"
          onClick={next}
          aria-label="Next"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
          style={{ background: 'rgba(255,255,255,0.92)', color: '#111', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}
        >
          <ChevronRightIcon className="w-[18px] h-[18px]" strokeWidth={1.8} />
        </button>
      )}

      {/* Dot indicators — bottom right, away from the text */}
      {showArrows && (
        <div className="absolute bottom-6 right-6 flex gap-2 z-10">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              type="button"
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === index ? '22px' : '6px',
                height: '6px',
                background: i === index ? '#fff' : 'rgba(255,255,255,0.45)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
